import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import roomBookingAPI from '../../services/roomBookingAPI';
import actionBtn from '../common/actionButtonStyles';

// ── Helpers ───────────────────────────────────────────────────────────────────

const HKT = 'Asia/Hong_Kong';

const fmt = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('zh-TW', {
    timeZone: HKT,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

// Convert a UTC ISO string to a datetime-local value in HKT
const toHKTInput = (iso) => {
  if (!iso) return '';
  // sv-SE locale gives "YYYY-MM-DD HH:MM:SS" — perfect for slicing
  return new Date(iso)
    .toLocaleString('sv-SE', { timeZone: HKT })
    .slice(0, 16)
    .replace(' ', 'T');
};

// Interpret a datetime-local value as HKT and convert to UTC ISO
const hktInputToISO = (val) => new Date(val + '+08:00').toISOString();

// Monday and Sunday of the current HKT week
const hktWeekRange = () => {
  const hktMs = Date.now() + 8 * 3600 * 1000;
  const day = new Date(hktMs).getUTCDay(); // 0=Sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monMs = hktMs + diffToMon * 86400000;
  const sunMs = monMs + 6 * 86400000;
  return {
    from: new Date(monMs).toISOString().slice(0, 10),
    to:   new Date(sunMs).toISOString().slice(0, 10),
  };
};

const STATUS_LABELS = { approved: '已確認', pending: '待審批', cancelled: '已取消' };
const STATUS_COLORS = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

// ── Booking Modal ─────────────────────────────────────────────────────────────

const EMPTY_BOOKING = {
  room_id: '',
  title: '',
  organizer_name: '',
  start_time: '',
  end_time: '',
  notes: '',
};

function BookingModal({ booking, rooms, onClose, onSaved }) {
  const dispatch = useDispatch();
  const isEdit = !!booking?.id;
  const [form, setForm] = useState(
    booking
      ? {
          room_id: booking.room_id,
          title: booking.title,
          organizer_name: booking.organizer_name,
          start_time: toHKTInput(booking.start_time),
          end_time: toHKTInput(booking.end_time),
          notes: booking.notes || '',
        }
      : { ...EMPTY_BOOKING }
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.room_id) return dispatch(addNotification({ type: 'error', message: '請選擇房間' }));
    if (!form.start_time || !form.end_time)
      return dispatch(addNotification({ type: 'error', message: '請填寫開始及結束時間' }));
    if (new Date(form.end_time + '+08:00') <= new Date(form.start_time + '+08:00'))
      return dispatch(addNotification({ type: 'error', message: '結束時間必須晊於開始時間' }));

    const payload = {
      room_id: Number(form.room_id),
      title: form.title,
      organizer_name: form.organizer_name,
      start_time: hktInputToISO(form.start_time),
      end_time: hktInputToISO(form.end_time),
      notes: form.notes || null,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await roomBookingAPI.updateBooking(booking.id, payload);
        dispatch(addNotification({ type: 'success', message: '預訂已更新' }));
      } else {
        await roomBookingAPI.createBooking(payload);
        dispatch(addNotification({ type: 'success', message: '預訂成功' }));
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || '操作失敗，請重試';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{isEdit ? '編輯預訂' : '新增預訂'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">房間 *</label>
            <select
              required
              value={form.room_id}
              onChange={(e) => set('room_id', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">請選擇房間</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.capacity ? ` (容納 ${r.capacity} 人)` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">預訂事由 *</label>
            <input
              required
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="例：主日崇拜排練"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">負責人 *</label>
            <input
              required
              type="text"
              value={form.organizer_name}
              onChange={(e) => set('organizer_name', e.target.value)}
              placeholder="負責人姓名"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">開始時間 *</label>
              <input
                required
                type="datetime-local"
                value={form.start_time}
                onChange={(e) => set('start_time', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">結束時間 *</label>
              <input
                required
                type="datetime-local"
                value={form.end_time}
                onChange={(e) => set('end_time', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">備註</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? '儲存中…' : isEdit ? '更新預訂' : '確認預訂'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Room Management Modal ─────────────────────────────────────────────────────

const EMPTY_ROOM = { name: '', capacity: '', description: '', location: '', is_active: true };

function RoomModal({ room, onClose, onSaved }) {
  const dispatch = useDispatch();
  const isEdit = !!room?.id;
  const [form, setForm] = useState(
    room
      ? { name: room.name, capacity: room.capacity ?? '', description: room.description ?? '', location: room.location ?? '', is_active: room.is_active }
      : { ...EMPTY_ROOM }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      capacity: form.capacity !== '' ? Number(form.capacity) : null,
      description: form.description || null,
      location: form.location || null,
      is_active: form.is_active,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await roomBookingAPI.updateRoom(room.id, payload);
        dispatch(addNotification({ type: 'success', message: '房間已更新' }));
      } else {
        await roomBookingAPI.createRoom(payload);
        dispatch(addNotification({ type: 'success', message: '房間已新增' }));
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || '操作失敗，請重試';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{isEdit ? '編輯房間' : '新增房間'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">房間名稱 *</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="例：主堂"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">容納人數</label>
              <input
                type="number"
                min="1"
                value={form.capacity}
                onChange={(e) => set('capacity', e.target.value)}
                placeholder="不限"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">位置</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder="例：3樓"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded"
            />
            <span className="text-sm text-gray-700">啟用此房間</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? '儲存中…' : isEdit ? '更新' : '新增'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const RoomBookingPage = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' | 'rooms'
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterRoom, setFilterRoom] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(() => hktWeekRange().from);
  const [filterDateTo, setFilterDateTo] = useState(() => hktWeekRange().to);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchText, setSearchText] = useState('');

  // Modals
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'booking'|'room', id, name }

  // ── Data loaders ─────────────────────────────────────────────────────────

  const loadRooms = useCallback(async () => {
    try {
      const res = await roomBookingAPI.listRooms(true);
      setRooms(res.data);
    } catch {
      dispatch(addNotification({ type: 'error', message: '無法載入房間列表' }));
    }
  }, [dispatch]);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterRoom) params.room_id = filterRoom;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterStatus) params.status = filterStatus;
      const res = await roomBookingAPI.listBookings(params);
      setBookings(res.data.items);
      setTotalBookings(res.data.total);
    } catch {
      dispatch(addNotification({ type: 'error', message: '無法載入預訂記錄' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, filterRoom, filterDateFrom, filterDateTo, filterStatus]);

  useEffect(() => { loadRooms(); }, [loadRooms]);
  useEffect(() => { if (activeTab === 'bookings') loadBookings(); }, [activeTab, loadBookings]);

  // ── Delete handlers ───────────────────────────────────────────────────────

  const handleDeleteBooking = async () => {
    try {
      await roomBookingAPI.deleteBooking(deleteTarget.id);
      dispatch(addNotification({ type: 'success', message: '預訂已刪除' }));
      setDeleteTarget(null);
      loadBookings();
    } catch {
      dispatch(addNotification({ type: 'error', message: '刪除失敗' }));
    }
  };

  const handleDeleteRoom = async () => {
    try {
      await roomBookingAPI.deleteRoom(deleteTarget.id);
      dispatch(addNotification({ type: 'success', message: '房間已刪除' }));
      setDeleteTarget(null);
      loadRooms();
    } catch (err) {
      const msg = err.response?.data?.detail || '刪除失敗';
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  };

  const activeRooms = rooms.filter((r) => r.is_active);

  const displayedBookings = searchText.trim()
    ? bookings.filter((b) => {
        const q = searchText.trim().toLowerCase();
        return (
          b.title?.toLowerCase().includes(q) ||
          b.organizer_name?.toLowerCase().includes(q)
        );
      })
    : bookings;

  const clearFilters = () => {
    setFilterRoom('');
    setFilterDateFrom(hktWeekRange().from);
    setFilterDateTo(hktWeekRange().to);
    setFilterStatus('');
    setSearchText('');
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {[
            { id: 'bookings', label: '預訂記錄' },
            { id: 'rooms', label: '管理房間' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Bookings Tab ── */}
      {activeTab === 'bookings' && (
        <div className="space-y-4">
          {/* Filters + New button */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">房間</label>
                <select
                  value={filterRoom}
                  onChange={(e) => setFilterRoom(e.target.value)}
                  className="border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">全部房間</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">開始日期</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">結束日期</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">狀態</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">全部</option>
                  <option value="pending">待審批</option>
                  <option value="approved">已確認</option>
                  <option value="cancelled">已取消</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">搜尋事由/負責人</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="輸入事由或負責人…"
                    className="border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
                  />
                  {searchText && (
                    <button
                      onClick={() => setSearchText('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2 items-end">
                <button
                  onClick={loadBookings}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  搜尋
                </button>
                <button
                  onClick={clearFilters}
                  className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  清除篩選
                </button>
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => { setEditingBooking(null); setBookingModalOpen(true); }}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  新增預訂
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-500">載入中…</div>
            ) : displayedBookings.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {bookings.length > 0 ? '沒有符合搜尋條件的記錄' : '暫無預訂記錄'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['房間', '事由', '負責人', '開始時間', '結束時間', '狀態', '操作'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayedBookings.map((b) => (
                      <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {b.room?.name ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{b.title}</td>
                        <td className="px-4 py-3 text-gray-600">{b.organizer_name}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(b.start_time)}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(b.end_time)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                            {STATUS_LABELS[b.status] ?? b.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 flex-wrap">
                            {b.status === 'pending' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await roomBookingAPI.updateBooking(b.id, { status: 'approved' });
                                    dispatch(addNotification({ type: 'success', message: '已批准預訂' }));
                                    loadBookings();
                                  } catch { dispatch(addNotification({ type: 'error', message: '操作失敗' })); }
                                }}
                                className={actionBtn.approve}
                              >
                                批准
                              </button>
                            )}
                            <button
                              onClick={() => { setEditingBooking(b); setBookingModalOpen(true); }}
                              className={actionBtn.edit}
                            >
                              編輯
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ type: 'booking', id: b.id, name: b.title })}
                              className={actionBtn.delete}
                            >
                              刪除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
                  {searchText.trim()
                    ? `顯示 ${displayedBookings.length} / ${totalBookings} 筆記錄`
                    : `共 ${totalBookings} 筆記錄`}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Rooms Tab ── */}
      {activeTab === 'rooms' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => { setEditingRoom(null); setRoomModalOpen(true); }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              新增房間
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {rooms.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                尚未新增任何房間
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['房間名稱', '位置', '容納人數', '描述', '狀態', '操作'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rooms.map((room) => (
                      <tr key={room.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{room.name}</td>
                        <td className="px-4 py-3 text-gray-600">{room.location ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{room.capacity ?? '不限'}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{room.description ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${room.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                            {room.is_active ? '啟用' : '停用'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setEditingRoom(room); setRoomModalOpen(true); }}
                              className={actionBtn.edit}
                            >
                              編輯
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ type: 'room', id: room.id, name: room.name })}
                              className={actionBtn.delete}
                            >
                              刪除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {bookingModalOpen && (
        <BookingModal
          booking={editingBooking}
          rooms={activeRooms}
          onClose={() => setBookingModalOpen(false)}
          onSaved={() => { setBookingModalOpen(false); loadBookings(); }}
        />
      )}

      {/* Room Modal */}
      {roomModalOpen && (
        <RoomModal
          room={editingRoom}
          onClose={() => setRoomModalOpen(false)}
          onSaved={() => { setRoomModalOpen(false); loadRooms(); }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">確認刪除</h3>
            <p className="text-gray-600 text-sm mb-6">
              確定要刪除「<strong>{deleteTarget.name}</strong>」嗎？此操作無法復原。
            </p>
            <div className="flex gap-3">
              <button
                onClick={deleteTarget.type === 'booking' ? handleDeleteBooking : handleDeleteRoom}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                確認刪除
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBookingPage;
